import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { ButtonProps } from '@/lib/base/buttons/button';
import { Button } from '@/lib/base/buttons/button';

import { useFormContext } from './form-context';

// =============================================================================
// ErrorTooltipItem — clickable field link inside the disabled-button tooltip
// =============================================================================

interface ErrorTooltipItemProps {
  name: string;
  label: string;
  error: string;
  touchAll: () => void;
  scrollToField: (name: string) => void;
}

function ErrorTooltipItem({
  name,
  label,
  error,
  touchAll,
  scrollToField,
}: ErrorTooltipItemProps) {
  const handleClick = useCallback(() => {
    touchAll();
    requestAnimationFrame(() => scrollToField(name));
  }, [touchAll, scrollToField, name]);

  return (
    <li>
      <span>
        <button
          type='button'
          className='underline underline-offset-2 cursor-pointer'
          onClick={handleClick}
        >
          {label}
        </button>
        : {error}
      </span>
    </li>
  );
}

// =============================================================================
// Types
// =============================================================================

export interface SubmitButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Called only when the form is valid. If the form is invalid, touchAll + scrollToField run instead. */
  onClick?: () => void | Promise<void>;
  /**
   * Additional disabled tooltip content shown alongside the error summary.
   * Merged with the auto-generated error list when the form is invalid.
   */
  disabledTooltip?: ReactNode;
  /**
   * Heading shown above the field-error list in the disabled tooltip when the
   * form has field errors (and no submitError). Defaults to a generic
   * "Your form has errors"; provide a context-specific message such as
   * "This email isn't ready to send yet".
   */
  errorSummaryTitle?: string;
}

// =============================================================================
// SubmitButton
// =============================================================================

export function SubmitButton({
  onClick,
  isDisabled,
  disabledTooltip: externalTooltip,
  errorSummaryTitle = 'Your form has errors',
  ...buttonProps
}: SubmitButtonProps) {
  const {
    touchAll,
    isValid,
    errorSummary,
    scrollToField,
    submitError,
    setSubmitError,
  } = useFormContext();

  // Local-only fallback message for the defensive branch below. Deliberately
  // NOT the shared FormProvider submitError — several SubmitButton consumers
  // (e.g. CrmCreateForm, EditEntityTypeSlideout, StagesSlideout)
  // already render submitError in their own role="alert" banner, so reusing
  // it here would double-render the message for every real submit failure.
  const [fallbackError, setFallbackError] = useState<string | null>(null);

  // When only submitError is blocking (no field errors), the button should
  // still be clickable so the user can retry without editing a field first.
  const hasFieldErrors = errorSummary.length > 0;
  const onlySubmitError = !!submitError && !hasFieldErrors;

  const handleClick = useCallback(() => {
    // Clear any stale submitError so the user can retry after a transient failure
    if (submitError) {
      setSubmitError(null);
    }
    if (fallbackError) {
      setFallbackError(null);
    }

    touchAll();

    // Re-check validity after clearing submitError — if only submitError was
    // blocking, the form is now valid and we can proceed.
    if (isValid || onlySubmitError) {
      onClick?.();
    } else if (errorSummary.length > 0) {
      // Scroll to the first errored field after touchAll triggers a re-render
      requestAnimationFrame(() => {
        scrollToField(errorSummary[0].name);
      });
    } else {
      // Defense-in-depth: invalid form with no field errors and no existing
      // submitError — surface a visible, announced message so the click is
      // never a no-op. Uses local fallbackError (not submitError) so the
      // message renders independent of the button's disabled/enabled state.
      setFallbackError('Please review the form before continuing.');
    }
  }, [
    touchAll,
    isValid,
    onlySubmitError,
    onClick,
    errorSummary,
    scrollToField,
    submitError,
    setSubmitError,
    fallbackError,
  ]);

  // Build error tooltip content from errorSummary and/or submitError.
  //
  // Deliberately keyed on hasFieldErrors alone, NOT `|| submitError`: when
  // onlySubmitError is true the button stays enabled, so Button's Tooltip
  // wrapper renders with isDisabled={!disabled}=true and never shows this
  // content anyway. Returning non-`externalTooltip` content here would still
  // flip Button's disabledTooltip prop from undefined to a ReactNode on every
  // failed submit, which flips Button's internal isGated from false to true
  // and remounts its single AriaButton — dropping the DOM node a test (or a
  // user) is holding onto mid-retry. See CrmCreateForm's "allows
  // retrying after a server error" test.
  const errorTooltipContent = useMemo(() => {
    const hasFieldErrors = errorSummary.length > 0;
    if (!hasFieldErrors) {
      return externalTooltip;
    }
    return (
      <div className='space-y-1'>
        {submitError && <span>{submitError}</span>}
        {hasFieldErrors && (
          <>
            {!submitError && <span>{errorSummaryTitle}</span>}
            <ul className='list-none space-y-0.5 pl-0'>
              {errorSummary.map(({ name, label, error }) => (
                <ErrorTooltipItem
                  key={name}
                  name={name}
                  label={label}
                  error={error}
                  touchAll={touchAll}
                  scrollToField={scrollToField}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }, [
    errorSummary,
    submitError,
    touchAll,
    scrollToField,
    externalTooltip,
    errorSummaryTitle,
  ]);

  // When only submitError blocks (no field errors), keep the button enabled
  // so the user can click to retry. Field errors still disable the button.
  const formDisabled = onlySubmitError ? false : !isValid;

  return (
    <>
      <Button
        {...buttonProps}
        type='button'
        onClick={handleClick}
        isDisabled={isDisabled || formDisabled}
        disabledTooltip={errorTooltipContent}
        data-state={buttonProps.isLoading ? 'saving' : undefined}
      />
      {fallbackError && (
        <div
          role='alert'
          className='mt-2 rounded-lg bg-[var(--color-bg-error-primary)] p-3 text-body-small text-[var(--color-text-error-primary)]'
        >
          {fallbackError}
        </div>
      )}
    </>
  );
}
